import AccountMenu from '@atlassian/account-menu/nav3/account-menu-nav3';
import React, { useState } from 'react';
import { FormattedMessage } from 'react-intl';
import { Box } from '@atlaskit/primitives/box';
import { Inline } from '@atlaskit/primitives/inline';
import { xcss } from '@atlaskit/primitives/xcss';
import ButtonItem from '@atlaskit/menu/button-item';
import Section from '@atlaskit/menu/section';
import { TourSpotlightTarget } from '../tour';
import { WHATS_NEW_MENU_ITEM_SPOTLIGHT_TARGET } from '../navigation/spotlightTargets';
import MegaphoneIcon from '@atlaskit/icon/core/megaphone';
import FeedbackIcon from '@atlaskit/icon/core/feedback';
import { useNavigate } from 'react-router-dom';
import { ThemeColorModes } from '@atlaskit/tokens/theme-config';
import {
	ANALYTICS_UI_SUBJECTS,
	ANALYTICS_TRACK_EVENT_ACTION_SUBJECTS,
} from '../../analytics/analyticsEvents';
import { useSendAnalyticEvent } from '../../analytics/analyticsClient';
import { useThemeContext } from '../../hooks/useThemeContext';
import { getAtlassianHomeUrl, getRovoExtensionWebBaseUrl } from '../../config';
import { UserAccount } from '../../clients/gateway-client/types';
import { announcements } from '../../data/announcements';
import { messages } from './messages';
import FeedbackModal from '../feedback/FeedbackModal';
import { Site } from '../../hooks/useSite';
import { useCurrentScreenName } from '../../hooks/useCurrentScreenName';
import { setLocalStorageItemRawString } from '../../clients/localStorage/localStorageClient';
import { SessionReplayUGC } from '../session-replay-ugc';

const blockStyles = xcss({ borderRadius: 'radius.xsmall' });

const profileContainerStyles = xcss({
	cursor: 'pointer',
});

const redDotStyles = xcss({
	position: 'absolute',
	top: 'space.0',
	right: 'space.0',
	width: '10px',
	height: '10px',
	backgroundColor: 'color.background.danger.bold',
	pointerEvents: 'none',
});

const profileWrapperStyles = xcss({
	position: 'relative',
});

export const Profile = ({
	onContextMenuModalComplete,
	onContextMenuModalOpen,
	showContextMenuModalDot,
	showWhatsNewButton,
	site,
	userAccount,
}: {
	onContextMenuModalComplete?: () => void;
	onContextMenuModalOpen?: () => void;
	showContextMenuModalDot?: boolean;
	showWhatsNewButton: boolean;
	site: Site | undefined;
	userAccount: UserAccount;
}): React.JSX.Element => {
	const currentScreenName = useCurrentScreenName();

	const rovoLogoutUrl = `${getRovoExtensionWebBaseUrl()}/extension/log-out`;
	const LAST_SEEN_ID = 'lastSeenAnnouncementId';

	const navigate = useNavigate();
	const { localThemeColorMode, setLocalThemeColorMode } = useThemeContext();
	const sendAnalyticEvent = useSendAnalyticEvent();

	const [isFeedbackModalOpen, setIsFeedbackModalOpen] = useState(false);

	const showFeedbackButton = userAccount.accountId && site;

	const handleFeedbackClick = (): void => {
		setIsFeedbackModalOpen(true);
		// https://data-portal.internal.atlassian.com/analytics/registry/73849
		sendAnalyticEvent({
			eventType: 'ui',
			event: {
				action: 'click',
				actionSubject: ANALYTICS_UI_SUBJECTS.FEEDBACK_BUTTON,
				source: currentScreenName,
			},
			userId: userAccount.accountId,
		});
	};

	const handleWhatsNewClick = (): void => {
		// https://data-portal.internal.atlassian.com/analytics/registry/77235
		sendAnalyticEvent({
			eventType: 'ui',
			event: {
				action: 'click',
				source: currentScreenName,
				actionSubject: ANALYTICS_UI_SUBJECTS.OPEN_MENU_TAB_BUTTON,
			},
			userId: userAccount.accountId,
		});
		if (announcements.length > 0) {
			setLocalStorageItemRawString(LAST_SEEN_ID, announcements[0].id);
		}

		// https://data-portal.internal.atlassian.com/analytics/registry/79796
		sendAnalyticEvent({
			eventType: 'track',
			event: {
				action: 'navigate',
				actionSubject: ANALYTICS_TRACK_EVENT_ACTION_SUBJECTS.WHATS_NEW,
				source: currentScreenName,
			},
			userId: userAccount.accountId,
		});

		navigate('/extension/menu');
		onContextMenuModalComplete?.();
	};

	const profileUrl = site
		? `${getAtlassianHomeUrl()}/o/${site.workspace.organisationId}/people/${userAccount.accountId}?cloudId=${site.workspace.cloudId}`
		: `${getAtlassianHomeUrl()}/people/me`;

	const handleViewProfileClick = (
		event: React.MouseEvent<Element, MouseEvent> | React.KeyboardEvent<Element>,
	): void => {
		// The default behavior will load the profile page as an iframe, which we want to avoid.
		event.preventDefault();
		window.open(profileUrl, '_blank');
	};

	const handleThemeChange = (theme: ThemeColorModes): void => {
		setLocalThemeColorMode(theme);
		// https://data-portal.internal.atlassian.com/analytics/registry/86671
		sendAnalyticEvent({
			eventType: 'track',
			event: {
				action: 'switched',
				actionSubject: ANALYTICS_TRACK_EVENT_ACTION_SUBJECTS.THEME,
				source: currentScreenName,
			},
			userId: userAccount.accountId,
		});
	};

	const extraMenuItems = [
		showWhatsNewButton && (
			<TourSpotlightTarget key="whats-new" name={WHATS_NEW_MENU_ITEM_SPOTLIGHT_TARGET}>
				<ButtonItem iconBefore={<MegaphoneIcon label="Menu" />} onClick={handleWhatsNewClick}>
					<FormattedMessage {...messages.whatsNew} />
				</ButtonItem>
			</TourSpotlightTarget>
		),
		showFeedbackButton && (
			<ButtonItem
				key="give-feedback"
				iconBefore={<FeedbackIcon label="feedback" />}
				onClick={handleFeedbackClick}
			>
				<FormattedMessage {...messages.giveFeedback} />
			</ButtonItem>
		),
	].filter(Boolean);

	const finalMenuItems: React.ReactNode[] =
		extraMenuItems.length > 0
			? [
					<Section key="extra-menu-items" hasSeparator>
						{extraMenuItems}
					</Section>,
				]
			: [];

	return (
		<>
			<SessionReplayUGC>
				<Box testId="profile-container" xcss={blockStyles}>
					<Inline alignInline="end" space="space.050">
						<Box xcss={profileWrapperStyles}>
							<Box
								onClick={showContextMenuModalDot ? onContextMenuModalOpen : undefined}
								testId="profile-avatar-container"
								xcss={profileContainerStyles}
							>
								<AccountMenu
									currentTheme={localThemeColorMode}
									email={userAccount.email}
									extraMenuItems={finalMenuItems}
									logOutLink={rovoLogoutUrl}
									name={userAccount.name}
									onThemeChange={handleThemeChange}
									onViewProfileClick={handleViewProfileClick}
									picture={userAccount.picture}
									testId="account-menu"
									viewProfileLink={profileUrl}
								/>
							</Box>
							{showContextMenuModalDot && (
								<Box
									// eslint-disable-next-line @atlaskit/design-system/ensure-design-token-usage
									style={{ borderRadius: '50%' }}
									testId="context-menu-modal-dot"
									xcss={redDotStyles}
								/>
							)}
						</Box>
					</Inline>
				</Box>
			</SessionReplayUGC>
			{showFeedbackButton && (
				<FeedbackModal
					setShowModal={(showModal: boolean) => setIsFeedbackModalOpen(showModal)}
					showModal={isFeedbackModalOpen}
					site={site}
					userAccount={userAccount}
				/>
			)}
		</>
	);
};
